import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/core';
import { Checkmark, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import IconIndicator from 'components/icons/IconIndicator';
import useEVChargingPricingInfo from 'hooks/useEvChargingPricingInfo';
import useGetPoolingInfo from 'hooks/useGetPoolingInfo';
import useParkingPricingInfo from 'hooks/useParkingPricingInfo';
import useTariffs from 'hooks/useTariffs';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useActiveElementStore } from 'stores/activeElementStore';
import { useParkzoneStore } from 'stores/parkzoneStore';
import styled from 'styled-components/native';
import { ChargingStation } from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import { truncateText } from 'utils/commonUtils';
import { resolveSegmentTitle } from 'utils/tariffUtils';
import ZoneDetailsRow from '../ZoneDetailsRow';

const RighItemContainer = styled.View`
  gap: 5px;
  flex-direction: row;
  align-items: center;
`;

const Tariff = ({
  chargingStations,
  borderTop,
  headlineTitle,
  borderBottom,
  color,
  padding,
}: {
  /* EV charging tariff */
  chargingStations?: ChargingStation[];
  borderTop?: boolean;
  headlineTitle?: boolean;
  borderBottom?: boolean;
  color?: string;
  padding?: number;
}) => {
  const { t } = useTranslation();
  const selectedTab = useParkzoneStore((state) => state.selectedParkingZoneTab);
  const selectedTariffUid = useParkzoneStore((state) => state.selectedTariffId);
  const { navigate: navigatePark } = useNavigation<ParkNavProps>();
  const parkingZoneUid = useActiveElementStore((state) => state.activeElement);
  const { pricingInfoLoading, pricingSegments } =
    useParkingPricingInfo(parkingZoneUid);
  const selectedTariff = resolveSegmentTitle(
    pricingSegments,
    selectedTariffUid
  );
  useTariffs('cache-first');
  const selectedType = useActiveElementStore(
    (state) => state.activeElementType
  );
  const selectedChargingSegment = useParkzoneStore(
    (state) => state.selectedChargingPricingInfoSegment
  );
  const { poolingGroup } = useGetPoolingInfo(parkingZoneUid);

  const selectedChargingStation = useMemo(() => {
    return chargingStations?.find(
      (chargingStation) =>
        chargingStation.id ===
        useActiveElementStore.getState().activeElementSecondaryUid
    );
  }, [chargingStations]);

  const { pricingSegmentsEv } = useEVChargingPricingInfo({
    chargingStations: chargingStations ?? [],
  });

  const segmentsAvailable = useMemo(() => {
    const chargingSegments = pricingSegmentsEv.length ?? 0;
    const parkingSegments = pricingSegments?.length ?? 0;
    if (selectedTab === 'CHARGE') {
      return chargingSegments;
    } else if (
      selectedTab === 'PARK_AND_CHARGE' &&
      selectedType === 'PARK_AND_CHARGE'
    ) {
      return parkingSegments + chargingSegments;
    }
    return parkingSegments;
  }, [selectedTab, pricingSegmentsEv, pricingSegments, selectedType]);

  const hasNoSegmentsAvailable =
    segmentsAvailable < 1 &&
    selectedChargingStation?.status !== 'AVAILABLE' &&
    !pricingInfoLoading;

  const titleText = () => {
    if (poolingGroup) {
      return t('parking.tariff.pooling');
    }
    const tariffName =
      selectedTab === 'CHARGE'
        ? selectedChargingSegment?.title
        : selectedTariff;
    if (selectedTab === 'PARK_AND_CHARGE') {
      return `${t('parking.tariff.typeParkAndCharge')} ${t(
        'parking.tariff.tariff'
      )}`;
    }
    return tariffName
      ? `${truncateText(tariffName, 19)} ${t('parking.tariff.tariff')}`
      : '';
  };

  const showCheckmark = () => {
    const isPrivateParkingTariff = pricingSegments?.some(
      (s) =>
        s?.uid === selectedTariffUid &&
        s?.accessType === 'PRIVATE' &&
        selectedTab === 'PARK'
    );
    const isPrivateChargingTariff =
      selectedChargingSegment?.accessType === 'PRIVATE' &&
      selectedTab === 'CHARGE';
    return isPrivateParkingTariff || isPrivateChargingTariff;
  };

  const RightItem = useMemo(() => {
    const showSegmentsAvailable =
      selectedTab === 'PARK_AND_CHARGE'
        ? segmentsAvailable > 2
        : segmentsAvailable > 1;
    return (
      <RighItemContainer>
        <Meta color={theme.colors.boost.green}>
          {t('parking.tariff.title')}
        </Meta>
        {showSegmentsAvailable && !poolingGroup && (
          <IconIndicator
            fullSize
            indicatorType="success"
            indicatorNumber={segmentsAvailable}
          />
        )}
      </RighItemContainer>
    );
  }, [t, segmentsAvailable, selectedTab, poolingGroup]);

  const onPress = () => {
    if (poolingGroup) {
      return navigatePark('poolingRates');
    }
    navigatePark('parkingTariffs');
  };

  if (hasNoSegmentsAvailable) {
    return null;
  }
  return (
    <ZoneDetailsRow
      title={titleText() || t('parking.tariff.selectTariff')}
      linkText={t('parking.tariff.title')}
      onPress={onPress}
      borderTop={borderTop}
      icon={<Icon name={'Document-Outlined'} />}
      rightItem={RightItem}
      loading={pricingInfoLoading}
      active={selectedTariffUid !== undefined}
      testIDprefix="tariff"
      headlineTitle={headlineTitle}
      borderBottom={borderBottom}
      chevronColor={color}
      linkColor={color}
      padding={padding}
      titleIcon={showCheckmark() && <Checkmark />}
    />
  );
};

export default Tariff;
